import { BaseKeyProvider, createKeyMaterialFromBuffer } from 'livekit-client';
import { MatrixRTCSession, MatrixRTCSessionEvent } from 'matrix-js-sdk/src/matrixrtc/MatrixRTCSession';

import logger from 'utils/logging/faro';

export type KeyMaterialFailureContext = { participantId: string; encryptionKeyIndex: number };

export class MatrixKeyProvider extends BaseKeyProvider {
  private rtcSession?: MatrixRTCSession;

  private onKeyMaterialFailed?: (error: unknown, ctx: KeyMaterialFailureContext) => void;

  public constructor() {
    super({ ratchetWindowSize: 0 });
  }

  // #8172: lets the call-encryption fallback hook react to key material failures
  // (signal S-B) without coupling the provider to React context.
  public setOnKeyMaterialFailed(cb: ((error: unknown, ctx: KeyMaterialFailureContext) => void) | undefined): void {
    this.onKeyMaterialFailed = cb;
  }

  public setRTCSession(rtcSession: MatrixRTCSession): void {
    if (this.rtcSession) {
      this.rtcSession.off(MatrixRTCSessionEvent.EncryptionKeyChanged, this.onEncryptionKeyChanged);
    }

    this.rtcSession = rtcSession;

    this.rtcSession.on(MatrixRTCSessionEvent.EncryptionKeyChanged, this.onEncryptionKeyChanged);

    // The new session could be aware of keys of which the old session wasn't,
    // so emit a key changed event.
    this.rtcSession.reemitEncryptionKeys();
  }

  public async clearRTCSession() {
    if (this.rtcSession) {
      this.rtcSession.off(MatrixRTCSessionEvent.EncryptionKeyChanged, this.onEncryptionKeyChanged);
      await this.rtcSession.leaveRoomSession();
      await this.rtcSession.stop();
      this.rtcSession = undefined;
    }
  }

  private onEncryptionKeyChanged = (
    encryptionKey: Uint8Array,
    encryptionKeyIndex: number,
    participantId: string
  ): void => {
    createKeyMaterialFromBuffer(encryptionKey as unknown as ArrayBuffer).then(
      (keyMaterial) => {
        logger.info(
          `Encryption key created and prepared to be set for participant: ${participantId}, index: ${encryptionKeyIndex} `
        );
        this.onSetEncryptionKey(keyMaterial, participantId, encryptionKeyIndex);
      },
      (e) => {
        logger.error(
          `Failed to create key material from buffer for livekit room=${this.rtcSession?.room.roomId} participantId=${participantId} encryptionKeyIndex=${encryptionKeyIndex}`,
          e
        );
        this.onKeyMaterialFailed?.(e, { participantId, encryptionKeyIndex });
      }
    );
  };
}
